/**
 * Runtime CSS isolation for the MFE host.
 *
 * Each remote bundles its OWN (version-drifting) cap-ui-library / antd CSS and injects
 * <link rel="stylesheet"> tags into <head> when its chunks load. webpack's
 * mini-css-extract never removes those links, so as you navigate remotes the global
 * antd/cap-ui-library rules from every visited remote pile up and clobber each other
 * (e.g. campaigns' `.ant-input-affix-wrapper { font-family: Roboto }` styling the
 * member-care page).
 *
 * Until cap-ui-library is shared as a singleton (the root fix), we keep only the
 * stylesheets of the ACTIVE SET of remotes enabled and disable the rest. On every route
 * change — and whenever a new remote stylesheet is injected — any remote stylesheet whose
 * owner is not in the active set is disabled. Host shell / font / third-party CSS (no
 * remote prefix) is never touched. Disabling is reversible, so navigating back re-enables.
 *
 * NESTING: the active set is NOT just the URL's remote. A remote can load another remote
 * (e.g. member-care embeds a campaigns widget) — that nested remote's CSS is legitimately
 * needed on the parent's route and must NOT be disabled. So the active set =
 *   { the URL's remote }  ∪  { every remote that has registered itself as mounted }.
 * Remotes announce mount/unmount so nested (non-route) remotes are covered:
 *   window.__mfeCssScope.register('<prefix>')   // on mount
 *   window.__mfeCssScope.unregister('<prefix>') // on unmount
 * or dispatch  new CustomEvent('mfe:app-mounted'|'mfe:app-unmounted', {detail:{app:'<prefix>'}}).
 * The URL's remote is always active even without registering (backward compatible).
 *
 * Two kinds of remote CSS are managed:
 *   1. <link rel="stylesheet">              — owner derived from the href path prefix.
 *   2. <style>/<link> with data-mfe-app     — owner read from the attribute. This covers
 *      CSS that a remote CHUNK injects at runtime as a <style> (webpack style-loader /
 *      insertCss), which has no href for the host to attribute by. The remote must stamp
 *      it (see per-remote tagging below); the host cannot reliably guess a <style>'s
 *      origin (it has no URL, and the host shell injects its own <style> tags too, so
 *      "active route at inject time" would wrongly disable host styles on a deep link).
 *
 * The data-mfe-app value must be the remote's serving-path prefix (e.g. "member-care/ui",
 * "tesseract/ui") — the same vocabulary as REMOTE_PREFIXES. Each remote can set it via its
 * style-loader `attributes` option, or a small head-insertion wrapper in its MFE bootstrap:
 *     { loader: 'style-loader', options: { attributes: { 'data-mfe-app': '<prefix>' } } }
 *
 * Host CSS is mostly left alone (shell / font / third-party <link>s, and untagged <style>
 * such as styled-components/antd cssinjs, which are content-hashed and largely
 * self-isolating). The ONE exception is the host's own home/ui PAGE CSS, code-split into
 * `hostpage-*` chunks: those pages don't render on remote routes, so their global
 * cap-ui-library rules would only leak — we disable them while a remote route is active
 * (see isHostPageCss). Fully isolating the shared cap-ui-library base CSS still needs the
 * singleton/version-align root fix, not a runtime toggle.
 */

// Path prefixes of the MFE remotes. KEEP IN SYNC with:
//   webapp/nginx/default.conf  (SPA-fallback regex) and app/mfe-navs.json routePaths.
// Sorted longest-first so a nested prefix (events-notification/ui) wins over a shorter
// one (events/ui).
const REMOTE_PREFIXES = [
  'analytics/v2',
  'ask-aira/ui',
  'audience-manager',
  'badges/ui',
  'campaigns/ui',
  'cards-configuration/ui',
  'coupons/ui',
  'creatives/ui',
  'customer-status-configuration/ui',
  'events-notification/ui',
  'events/ui',
  'experience/ui',
  'extensions/connectplus/ui',
  'extensions/neo/ui',
  'hierarchy/ui',
  'import/ui',
  'journey/ui',
  'launchpad/ui',
  'loyalty/ui',
  'member-care/ui',
  'rewards-catalog/ui',
  'search-criteria/ui',
  'tesseract/ui',
  'vulcan/ui',
].sort((a, b) => b.length - a.length);

// Prefixes learned at runtime from remotes that register() themselves (see below). This
// makes attribution robust to serving paths NOT in the seed list above (the seed is only a
// bootstrap so <link>s can be scoped before their remote registers). A remote's registered
// prefix is its real asset public path, which is exactly what appears in its CSS hrefs — so
// href attribution and mount tracking stay consistent without hardcoding every app.
const learned = new Set();
const allPrefixes = () =>
  [...new Set([...REMOTE_PREFIXES, ...learned])].sort((a, b) => b.length - a.length);

// Which remote a pathname belongs to (null = host shell / shared / third-party).
const matchPrefix = pathname => {
  const p = (pathname || '').replace(/^\/+/, '');
  return allPrefixes().find(pref => p === pref || p.startsWith(`${pref}/`)) || null;
};

const remoteOfHref = href => {
  try {
    return matchPrefix(new URL(href, window.location.origin).pathname);
  } catch (e) {
    return null;
  }
};

// Owner of a stylesheet node (null = host/shared/third-party — never touch):
//   explicit data-mfe-app tag wins (works for runtime-injected <style>), else the
//   <link>'s href path prefix.
const ownerOfNode = node => {
  const tagged = node.getAttribute('data-mfe-app');
  if (tagged) return matchPrefix(`/${tagged.replace(/^\/+/, '')}`);
  if (node.nodeName === 'LINK') return remoteOfHref(node.getAttribute('href'));
  return null;
};

// Host home/ui page CSS. These pages (Dashboard, Product, Entities, auth pages, …) are
// lazy-loaded under the `hostpage-*` chunk name (see webpackChunkName in their route
// imports), so their cap-ui-library CSS lands in a per-page async <link> named
// hostpage-<x>.<hash>.css. They render ONLY on host routes; on a remote route they are not
// on screen, so their global cap-ui-library/antd rules would only leak into the remote.
// webpack never removes the <link> once loaded, so we disable it here instead. Reversible —
// re-enabled the moment the URL returns to a host route.
const isHostPageCss = node =>
  node.nodeName === 'LINK' &&
  /(^|\/)hostpage-[^/]+\.css($|\?)/.test(node.getAttribute('href') || '');

// <link> supports `disabled`; a <style> does not, so deactivate it with media="not all".
const setDisabled = (node, disable) => {
  if (node.nodeName === 'LINK') {
    if (node.disabled !== disable) node.disabled = disable;
  } else {
    const media = disable ? 'not all' : 'all';
    if (node.getAttribute('media') !== media) node.setAttribute('media', media);
  }
};

// Remotes explicitly registered as mounted (handles nesting — a remote loaded by another
// remote, not reflected in the URL). Always combined with the URL's remote.
const mounted = new Set();

const activeApps = () => {
  const set = new Set(mounted);
  const routeApp = matchPrefix(window.location.pathname);
  if (routeApp) set.add(routeApp);
  return set;
};

const applyScope = () => {
  const active = activeApps();
  // Gate host page CSS on the URL's remote, NOT on `active` (which also includes nested
  // remotes). A host page that embeds a remote widget is still rendered and needs its CSS,
  // so only a genuine remote *route* means no host page is on screen.
  const routeIsRemote = matchPrefix(window.location.pathname) != null;
  const nodes = document.querySelectorAll(
    'link[rel="stylesheet"][href], link[data-mfe-app], style[data-mfe-app]',
  );
  nodes.forEach(node => {
    if (isHostPageCss(node)) {
      setDisabled(node, routeIsRemote); // host page CSS: keep on host routes, drop on remote routes
      return;
    }
    const owner = ownerOfNode(node);
    if (!owner) return; // host shell / font / third-party — never touch
    setDisabled(node, !active.has(owner)); // keep CSS for every active (incl. nested) remote
  });
};

// Normalize a registration value to a bare served prefix: accepts a full public-path URL
// ("https://host/coupons/ui/") or a path ("/coupons/ui") → "coupons/ui".
const normalize = app =>
  String(app || '')
    .replace(/^https?:\/\/[^/]+/, '')
    .replace(/^\/+|\/+$/g, '');
const register = app => {
  const p = normalize(app);
  if (!p) return;
  learned.add(p); // teach the host this remote's real serving prefix
  if (!mounted.has(p)) {
    mounted.add(p);
    applyScope();
  }
};
const unregister = app => {
  const p = normalize(app);
  if (p && mounted.delete(p)) applyScope();
};

let started = false;

export default function initMfeCssScope(history) {
  if (started || typeof document === 'undefined') return;
  started = true;

  // Expose the mount registry so remotes can announce nested loads (see header).
  window.__mfeCssScope = { register, unregister, apply: applyScope };
  window.addEventListener('mfe:app-mounted', e => register(e.detail && e.detail.app));
  window.addEventListener('mfe:app-unmounted', e => unregister(e.detail && e.detail.app));

  applyScope();

  // Re-apply on every route change.
  if (history && typeof history.listen === 'function') history.listen(applyScope);
  window.addEventListener('popstate', applyScope);
  window.addEventListener('hashchange', applyScope);

  // Remote CSS is injected asynchronously as chunks load — re-apply when new stylesheet
  // links appear so a just-loaded off-route remote's CSS is disabled immediately (no flash).
  const observer = new MutationObserver(mutations => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        const isStylesheetLink =
          node.nodeName === 'LINK' &&
          (node.getAttribute('rel') === 'stylesheet' || node.hasAttribute('data-mfe-app'));
        const isTaggedStyle = node.nodeName === 'STYLE' && node.hasAttribute('data-mfe-app');
        if (isStylesheetLink || isTaggedStyle) {
          applyScope();
          return;
        }
      }
    }
  });
  observer.observe(document.head, { childList: true });
}
